import { useEffect, useMemo, useState, useCallback } from "react";
import api, { errorMessage } from "../../lib/api";
import { Modal } from "../Modal";
import { FormField, inputClass, ActionButton, GhostButton, PillBadge } from "../Primitives";
import { SingleSelectDropdown } from "../SingleSelectDropdown";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

/** Convert ISO string to the value shape a <input type="datetime-local"> wants. */
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Manual attendance backfill modal — used by supervisors when a guard couldn't
 * clock in via the mobile app (dead phone, forgot device, etc.). Bypasses
 * geofence server-side. Restricted to supervisor+ roles.
 */
export function LogAttendanceModal({ onClose, onSaved, prefill = null }) {
  const [sites, setSites] = useState([]);
  const [guards, setGuards] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [onShiftOfficers, setOnShiftOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [siteId, setSiteId] = useState(prefill?.site_id || "");
  const [guardId, setGuardId] = useState(prefill?.guard_id || "");
  const [shiftId, setShiftId] = useState(prefill?.shift_id || "");
  const [checkIn, setCheckIn] = useState(prefill?.check_in ? toLocalInput(prefill.check_in) : "");
  const [checkOut, setCheckOut] = useState("");
  const [notes, setNotes] = useState("");
  // Default the "on-shift only" toggle OFF when this modal was pre-filled
  // from a specific row — the whole point of a backfill is that the guard
  // is NOT currently on-shift.
  const [guardsOnShiftOnly, setGuardsOnShiftOnly] = useState(!prefill?.guard_id);
  // When true, all three pickers are locked because the caller pre-selected
  // a specific row (e.g. clicking Log on a no-show attendance row).
  const isRowPrefill = !!(prefill?.shift_id && prefill?.guard_id && prefill?.site_id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, g, sh, o] = await Promise.all([
          api.get("/sites"),
          api.get("/guards"),
          api.get("/shifts"),
          api.get("/live-map/officers").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setSites(s.data || []);
        setGuards((g.data || []).filter((x) => (x.status || "active") !== "inactive" && !x.deactivation_date));
        setShifts(sh.data || []);
        setOnShiftOfficers(o.data || []);
      } catch (e) {
        if (!cancelled) toast.error(errorMessage(e, "Failed to load data"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Auto-default site to the supervisor's current shift if they have one.
    // Skip when we already have a full row-prefill from the caller.
    if (isRowPrefill) return () => { cancelled = true; };
    api.get("/mobile/active-shift").then((r) => {
      if (cancelled) return;
      const activeSiteId = r.data?.active?.site?.id;
      if (activeSiteId) setSiteId((prev) => prev || activeSiteId);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isRowPrefill]);

  // Sorted searchable options
  const siteOptions = useMemo(() => sites
    .map((s) => ({ value: s.id, label: s.name, sub: s.client_name || "" }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  [sites]);

  const guardOptions = useMemo(() => {
    if (guardsOnShiftOnly && siteId) {
      const ids = new Set(onShiftOfficers.filter((o) => o.site_id === siteId).map((o) => o.guard_id));
      return guards
        .filter((g) => ids.has(g.id))
        .map((g) => ({ value: g.id, label: g.name, sub: g.badge_no || "" }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return guards
      .map((g) => ({ value: g.id, label: g.name, sub: g.badge_no || "" }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [guardsOnShiftOnly, siteId, onShiftOfficers, guards]);

  // Shifts filtered by site + guard, sorted newest-first, capped for the dropdown.
  const shiftOptions = useMemo(() => {
    if (!siteId || !guardId) return [];
    return shifts
      .filter((s) => s.site_id === siteId && (s.guard_ids || []).includes(guardId))
      .sort((a, b) => (b.start_time || "").localeCompare(a.start_time || ""))
      .slice(0, 30)
      .map((s) => ({
        value: s.id,
        label: `${new Date(s.start_time).toLocaleString()} → ${new Date(s.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        sub: s.checked_in_at ? "already has attendance" : (s.status || "scheduled"),
      }));
  }, [shifts, siteId, guardId]);

  // Auto-prefill check-in with the shift's scheduled start when a shift is picked.
  useEffect(() => {
    if (!shiftId) return;
    const s = shifts.find((x) => x.id === shiftId);
    if (!s) return;
    if (!checkIn) setCheckIn(toLocalInput(s.start_time));
    // Do NOT prefill check-out — supervisor decides if the shift is done.
  }, [shiftId, shifts, checkIn]);

  const canSubmit = siteId && guardId && shiftId && checkIn && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/attendance/manual", {
        shift_id: shiftId,
        guard_id: guardId,
        check_in_time: new Date(checkIn).toISOString(),
        check_out_time: checkOut ? new Date(checkOut).toISOString() : null,
        notes: notes.trim() || null,
      });
      toast.success("Attendance logged");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Log Attendance" testId="log-attendance-modal">
      <div className="space-y-4">
        <div className="text-[11px] text-zinc-400 border border-[#27272A] bg-[#0f0f10] p-3 leading-snug">
          Backfill an attendance record for a  who couldn&rsquo;t clock in via the app.
          The record is flagged as a <span className="text-[#FF9F0A] font-bold">manual entry</span> in the audit log with your identity.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Site *">
            <SingleSelectDropdown
              testId="log-att-site"
              label="Select site…"
              options={siteOptions}
              value={siteId}
              onChange={(v) => { setSiteId(v); setGuardId(""); setShiftId(""); }}
              searchable
              triggerClassName="w-full"
            />
          </FormField>
          <FormField label="Officer *">
            <div className="space-y-1">
              <SingleSelectDropdown
                testId="log-att-guard"
                label={siteId ? "Select guard…" : "Pick a site first"}
                options={guardOptions}
                value={guardId}
                onChange={(v) => { setGuardId(v); setShiftId(""); }}
                searchable
                triggerClassName="w-full"
              />
              <label
                data-testid="log-att-guard-onshift-toggle"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-300"
              >
                <input
                  type="checkbox"
                  className="accent-[#FF3B30]"
                  checked={guardsOnShiftOnly}
                  onChange={(e) => setGuardsOnShiftOnly(e.target.checked)}
                  disabled={!siteId}
                />
                On-shift at this site only
              </label>
            </div>
          </FormField>
        </div>

        <FormField label="Shift *">
          <SingleSelectDropdown
            testId="log-att-shift"
            label={guardId ? (shiftOptions.length ? "Select shift…" : "No matching shifts") : "Pick a guard first"}
            options={shiftOptions}
            value={shiftId}
            onChange={setShiftId}
            searchable
            triggerClassName="w-full"
          />
          {shiftOptions.length > 0 && (
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
              Showing {shiftOptions.length} most-recent shift{shiftOptions.length === 1 ? "" : "s"} for this guard at this site.
            </div>
          )}
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Check-in time *">
            <input
              data-testid="log-att-check-in"
              type="datetime-local"
              className={inputClass}
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
            />
          </FormField>
          <FormField label="Check-out time (optional)">
            <input
              data-testid="log-att-check-out"
              type="datetime-local"
              className={inputClass}
              value={checkOut}
              onChange={(e) => setCheckOut(e.target.value)}
              min={checkIn || undefined}
            />
          </FormField>
        </div>

        <FormField label="Reason / notes (optional)">
          <textarea
            data-testid="log-att-notes"
            rows={2}
            className={inputClass}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Phone battery died at check-in — verified via radio."
          />
        </FormField>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#27272A]">
          <div className="flex-1">
            {loading && <PillBadge tone="default">Loading…</PillBadge>}
          </div>
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <ActionButton
            data-testid="log-att-submit"
            onClick={submit}
            disabled={!canSubmit}
          >
            <ClipboardCheck className="size-4" /> {saving ? "Saving…" : "Log Attendance"}
          </ActionButton>
        </div>
      </div>
    </Modal>
  );
}
